import React, { useState } from "react";
import { useLanguage, useT } from "../../lib/LanguageContext";
import {
  AFFILIATE_KINDS, COMMISSION_STATES, AFFILIATE_PATHS,
} from "../../affiliate/contract";
import { formatMoneyCode } from "../../lib/formatMoney";
import LoadFailure from "../../components/ui/LoadFailure";
// Cross-plane on purpose: these four modules are plane-agnostic (a paginated envelope, a
// presentational pill, two pure formatters, a code->sentence map that takes its key family
// as an argument). `gateway/components/GatewayWallet.jsx` already imports `admin/money.js`
// the same way — the alternative is a second copy that drifts.
import usePagedList from "../../hooks/usePagedList";
import CommissionStatusBadge from "../../admin/affiliate/CommissionStatusBadge";
import Pager from "../../components/ui/Pager";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import { fmtDate, fmtBps, EMPTY } from "../../admin/affiliate/format";
import ReverseCommissionDialog from "./ReverseCommissionDialog";
import { useScreenWrites } from "../screenWrites";

const KIND_KEYS = { saas: "saff.kind.saas", marketplace: "saff.kind.marketplace" };
const STATE_KEYS = {
  accrued: "saff.state.accrued",
  partially_reversed: "saff.state.partially_reversed",
  reversed: "saff.state.reversed",
};

/**
 * Every affiliate commission on the platform, with the three things an operator cannot act
 * without: whose it is, which ledger group carries it, and the reverse action.
 *
 * THE REVERSE BUTTON IS ABSENT, not merely disabled, on a row with nothing left to take
 * back: `AFFILIATE_COMMISSION_ALREADY_REVERSED` is what makes a second click safe, and a
 * control that can only refuse is a control that teaches its operator to ignore refusals.
 *
 * `affiliateTenantId` can be pinned from outside (the detail screen reuses this table), so
 * the console and the per-affiliate view are one projection and cannot state different
 * numbers.
 */
export default function CommissionsTable({ gatewayApi, affiliateTenantId = "" }) {
  // Every affiliate write is finance.write behind a step-up (minted with finance.write too).
  const { manage: canManage } = useScreenWrites("affiliate");
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [target, setTarget] = useState(null);

  const list = usePagedList(AFFILIATE_PATHS.adminCommissions, {
    ...(kind ? { kind } : {}),
    ...(status ? { status } : {}),
    ...(affiliateTenantId ? { affiliateTenantId } : {}),
  });
  const { reload } = list;

  const closeDialog = (didMutate) => {
    setTarget(null);
    if (didMutate) reload();
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="sa-affiliate-commissions">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-slate-200">
        <div>
          <h2 className="text-xl text-slate-800">{t("saff.commissions.title")}</h2>
          <p className="text-xs text-slate-400 mt-0.5">{t("saff.commissions.baseHint")}</p>
        </div>
        <div className="flex gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid="sa-commissions-kind-filter"
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">{t("saff.commissions.filterAllKinds")}</option>
            {AFFILIATE_KINDS.map((k) => <option key={k} value={k}>{t(KIND_KEYS[k])}</option>)}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="sa-commissions-status-filter"
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">{t("saff.commissions.filterAllStates")}</option>
            {COMMISSION_STATES.map((s) => <option key={s} value={s}>{t(STATE_KEYS[s])}</option>)}
          </select>
        </div>
      </div>

      {list.loadError ? (
        <div className="p-5">
          <LoadFailure
            message={affiliateErrorText(list.loadError, { t, tv, prefix: "saff", fallback: tv("errors.loadFailed") })}
            onRetry={reload}
            retryLabel={tv("actions.retry")}
            testId="sa-affiliate-commissions-load-error"
            retryTestId="sa-affiliate-commissions-retry"
          />
        </div>
      ) : list.loaded && list.rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm" data-testid="sa-affiliate-commissions-empty">
          {t("saff.commissions.empty")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2">{t("saff.commissions.colAffiliate")}</th>
                  <th className="text-left px-4 py-2">{t("saff.commissions.colCreatedAt")}</th>
                  <th className="text-left px-4 py-2">{t("saff.commissions.colKind")}</th>
                  <th className="text-left px-4 py-2">{t("saff.commissions.colCounterparty")}</th>
                  <th className="text-right px-4 py-2">{t("saff.commissions.colBase")}</th>
                  <th className="text-right px-4 py-2">{t("saff.commissions.colRate")}</th>
                  <th className="text-right px-4 py-2">{t("saff.commissions.colAmount")}</th>
                  <th className="text-right px-4 py-2">{t("saff.commissions.colReversed")}</th>
                  <th className="text-left px-4 py-2">{t("saff.commissions.colStatus")}</th>
                  <th className="text-left px-4 py-2">{t("saff.commissions.colGroup")}</th>
                  <th className="text-right px-4 py-2">{t("saff.commissions.colAction")}</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((c) => {
                  const remainder = (c.amountCents || 0) - (c.reversedCents || 0);
                  return (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`sa-commission-${c.id}`}>
                      <td className="px-4 py-2 text-slate-800">{c.affiliateName || c.affiliateTenantId}</td>
                      <td className="px-4 py-2 text-slate-500">{fmtDate(c.createdAt, lang)}</td>
                      <td className="px-4 py-2 text-slate-500">
                        {KIND_KEYS[c.kind] ? t(KIND_KEYS[c.kind]) : c.kind}
                        <span className="block text-[11px] text-slate-400">{c.productType || EMPTY}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-500">{c.counterpartyName || EMPTY}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                        {formatMoneyCode(c.baseCents, c.currency)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                        {t("saff.commissions.ratePct", { pct: fmtBps(c.rateBps) })}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {formatMoneyCode(c.amountCents, c.currency)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-rose-700">
                        {c.reversedCents ? formatMoneyCode(c.reversedCents, c.currency) : EMPTY}
                      </td>
                      <td className="px-4 py-2">
                        <CommissionStatusBadge
                          status={c.status}
                          label={STATE_KEYS[c.status] ? t(STATE_KEYS[c.status]) : c.status}
                          testId={`sa-commission-status-${c.id}`}
                        />
                      </td>
                      <td className="px-4 py-2 text-[11px] text-slate-400 font-mono break-all">{c.entryGroupId || EMPTY}</td>
                      <td className="px-4 py-2 text-right">
                        {canManage && remainder > 0 && (
                          <button
                            type="button"
                            onClick={() => setTarget(c)}
                            data-testid={`reverse-${c.id}`}
                            className="text-xs px-2 py-1 rounded border border-slate-200 text-rose-700"
                          >
                            {t("saff.commissions.reverse")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.page}
            pageSize={list.pageSize}
            total={list.total}
            shown={list.rows.length}
            onPage={list.setPage}
            infoText={(vars) => t("saff.page.info", vars)}
            prevLabel={t("saff.page.prev")}
            nextLabel={t("saff.page.next")}
            testId="sa-commissions-page"
            tone="slate"
          />
        </>
      )}

      <ReverseCommissionDialog
        commission={target}
        open={!!target}
        onClose={closeDialog}
        gatewayApi={gatewayApi}
        t={t}
        tv={tv}
      />
    </section>
  );
}
